import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  DatePicker,
  Space,
  Col,
  Select,
  Row,
  message,
  Tag,
  Tooltip,
  Tabs,
  Switch,
  Grid,
} from "antd";
import { useSelector } from "react-redux";
import { PlusCircleOutlined, EditOutlined } from "@ant-design/icons";
import { fetchPincode } from "../pincode/FetchPincode";
import { fetchStates } from "../state/FetchStates";
import { fetchCities } from "../city/FetchCities";
import { fetchCountries } from "../country/FetchCountries";
import { fetchLab } from "./FetchLab";
import moment from "moment";
import { selectBaseUrl, selectProdTestUrl } from "../../redux/slices/envSlice";
import {
  admin_create_lab,
  admin_update_lab,
  create_lab_address,
  create_lab_coordinates,
  create_lab_lims,
  create_lab_user,
  fetch_test_departments,
  update_lab_address,
  update_lab_coordinates,
  update_lab_lims,
  update_lab_user,
  create_camp_coordinates,
  update_camp_coordinates,
  fetch_camp_coordinates,
  create_camp_location,
} from "../../endpoints/API";
import GoogleMapsProvider from "../map/GoogleMapsProvider";
import AllLabViewPolygonMap from "../map/AllLabViewPolygonMap";
import LabMapWithPolygon from "../map/LabMap";
import CampMapPicker from "../map/CampMapPicker";

const { TabPane } = Tabs;
const { useBreakpoint } = Grid;

const LabPartner = () => {
  const user = useSelector((state) => state.user.user);
  const baseUrl = useSelector(selectBaseUrl);
  const prodTestUrl = useSelector(selectProdTestUrl);
  const isUser = useSelector((state) => state.user.user);

  const screens = useBreakpoint();
  const isMobile = !screens.sm;
  const isTablet = screens.sm && !screens.lg;

  // ── lab state ───────────────────────────────────────────────────────────────
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [isLimsModalOpen, setIsLimsModalOpen] = useState(false);
  const [selectedLab, setSelectedLab] = useState(null);
  const [form] = Form.useForm();
  const [addressForm] = Form.useForm();
  const [limsForm] = Form.useForm();
  const [pincode, setPincodes] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [lab, setLab] = useState([]);
  const [allLabCoordinates, setAllLabCoordinates] = useState([]);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [selectedLabForUser, setSelectedLabForUser] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [userForm] = Form.useForm();
  const [isModalOpenMap, setIsModalOpenMap] = useState(false);
  const [selectedLabId, setSelectedLabId] = useState(null);
  const [mapOpen, setMapOpen] = useState(false);
  const [selectedCoords, setSelectedCoords] = useState([]);
  const [selectedCoordinates, setSelectedCoordinates] = useState(null);
  const [islat, setIsLat] = useState();
  const [isLong, setIsLong] = useState();
  const [isEditAddressMode, setIsEditAddressMode] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [mode, setMode] = useState("add");
  const [refresh, setRefresh] = useState(false);
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  // ── camp state ──────────────────────────────────────────────────────────────
  const [campList, setCampList] = useState([]);
  const [campRefresh, setCampRefresh] = useState(false);
  const [isCampAddressModalOpen, setIsCampAddressModalOpen] = useState(false);
  const [campAddressForm] = Form.useForm();
  const [campAddressLoading, setCampAddressLoading] = useState(false);
  const [campSavedAddressId, setCampSavedAddressId] = useState(null);
  const [campSavedAddressLabel, setCampSavedAddressLabel] = useState("");
  const [isCampMapOpen, setIsCampMapOpen] = useState(false);
  const [campCoordinates, setCampCoordinates] = useState([]);
  const [campEnabled, setCampEnabled] = useState(true);
  const [campCoordLoading, setCampCoordLoading] = useState(false);
  const [campEditId, setCampEditId] = useState(null);
  const [campEditMode, setCampEditMode] = useState(false);

  // ── data load ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const getGlobalData = async () => {
      try {
        await fetchPincode(setPincodes, baseUrl, prodTestUrl);
        await fetchStates(setStates, baseUrl, prodTestUrl);
        await fetchCities(setCities, baseUrl, prodTestUrl);
        await fetchCountries(setCountries, baseUrl, prodTestUrl);
        await fetchLab((labsData) => {
          setLab(labsData);
          const transformed = labsData
            .filter((l) => l.territories?.length > 0)
            .map((l) => ({
              name: l.name,
              territories: l.territories.map((t) => ({
                id: t.id,
                coordinates: t.coordinates,
                test_department: t.test_department,
              })),
            }));
          setAllLabCoordinates(transformed);
        }, baseUrl, prodTestUrl);
        const res = await fetch_test_departments();
        setDepartments(res.data);
        const pathology = res.data.find((d) => d.name === "pathology");
        if (pathology) setSelectedDepartment(pathology.id);
      } catch (error) {
        message.error("Failed to fetch data.");
      }
    };
    getGlobalData();
  }, [refresh]);

  useEffect(() => {
    const loadCampCoordinates = async () => {
      try {
        const response = await fetch_camp_coordinates();
        const data = response.data;
        setCampList(Array.isArray(data) ? data : data.results ?? []);
      } catch (err) {
        message.error("Failed to fetch camp coordinates.");
      }
    };
    loadCampCoordinates();
  }, [campRefresh]);

  // ── lab coordinate handlers ─────────────────────────────────────────────────
  const handleSaveCoordinates = async (coordinates, departmentId) => {
    const payload = { labId: selectedLabId, coordinates, test_department: departmentId };
    try {
      const response = await create_lab_coordinates(payload);
      if (response.statusText === "OK") {
        message.success("Coordinates saved successfully!");
        setIsModalOpenMap(false);
      } else {
        message.error(response?.data?.message || "Failed to save coordinates.");
      }
    } catch (error) {
      message.error(error?.response?.data?.error || "An error occurred while saving coordinates.");
    } finally {
      setIsModalOpen(false);
      setRefresh(!refresh);
    }
  };

  const handleEditCoordinates = async (coordinates, departmentId) => {
    const payload = { labId: selectedLabId, coordinates, test_department: departmentId };
    try {
      const response = await update_lab_coordinates(payload);
      if (response.statusText === "OK") {
        message.success("Coordinates updated successfully!");
        setIsModalOpenMap(false);
      } else {
        message.error(response?.data?.message || "Failed to update coordinates.");
      }
    } catch (error) {
      message.error(error?.response?.data?.error || "An error occurred while updating coordinates.");
    } finally {
      setIsModalOpenMap(false);
      setRefresh(!refresh);
    }
  };

  // ── lab modal handlers ──────────────────────────────────────────────────────
  const showModal = () => setIsModalOpen(true);
  const handleCancel = () => { setIsModalOpen(false); form.resetFields(); };
  const handleOpenMap = (coordinates) => { setSelectedCoords(coordinates); setMapOpen(true); };
  const handleCloseMap = () => { setMapOpen(false); setSelectedCoords([]); };

  const handleEdit = (record) => {
    setIsEditMode(true);
    form.setFieldsValue({
      labName: record.name,
      onboardedOn: record.onboarded_on ? moment(record.onboarded_on) : null,
      terminatedOn: record.terminated_on ? moment(record.terminated_on) : null,
      contact: record.spoc_phone_numbers,
      aboutLab: record.lab_details,
    });
    setSelectedLab(record);
    setIsModalOpen(true);
  };

  const handleFormSubmit = async (values) => {
    const formattedValues = {
      labName: values.labName,
      onboardedOn: values.onboardedOn ? values.onboardedOn.format("YYYY-MM-DD") : null,
      terminatedOn: values.terminatedOn ? values.terminatedOn.format("YYYY-MM-DD") : null,
      contact: values.contact,
      aboutLab: values.aboutLab,
      created_by: user.user_id,
    };
    try {
      const response = isEditMode && selectedLab
        ? await admin_update_lab(selectedLab.id, formattedValues)
        : await admin_create_lab(formattedValues);
      if (response.statusText === "OK") {
        message.success(isEditMode ? "Lab updated successfully!" : "Lab created successfully!");
        setIsModalOpen(false);
        form.resetFields();
        setIsEditMode(false);
        await fetchLab(setLab, baseUrl, prodTestUrl);
      } else {
        message.error(response?.data?.error || "Failed to save lab data.");
      }
    } catch (error) {
      message.error(error?.response?.data?.error || "An unexpected error occurred.");
    }
  };

  const handleAddressCancel = () => { setIsAddressModalOpen(false); addressForm.resetFields(); };

  const handleFinishAddress = async (values) => {
    const payload = {
      location_id: selectedAddressId,
      lab_id: selectedLab?.id,
      address_identifier: values.address_identifier,
      country: values.country,
      state: values.state,
      city: values.city,
      pincode: values.pincode,
      apartment_road_area: values.apartment_road_area,
      house_flat_floor_no: values.house_flat_floor_no,
    };
    try {
      const response = isEditAddressMode
        ? await update_lab_address(payload)
        : await create_lab_address(payload);
      if (response.statusText === "OK") {
        message.success(isEditAddressMode ? "Address updated!" : "Address added!");
        await fetchLab(setLab, baseUrl, prodTestUrl);
        handleAddressCancel();
      } else {
        message.error(response?.data?.detail || "Failed to save address.");
      }
    } catch (error) {
      message.error(error?.response?.data?.error || "An unexpected error occurred.");
    }
  };

  const showLimsModal = (lab, lims = null) => {
    setSelectedLab(lab);
    if (lims) {
      limsForm.setFieldsValue({
        name: lims.lims_name || lims.name,
        code: lims.lims_code || lims.code || "",
        type: lims.lims_type || lims.type,
        patient_registration_url: lims.patient_registration_url || "",
        report_fetch_url: lims.report_fetch_url || "",
        fastest_webhook_url: lims.fastest_webhook_url || "",
      });
      setIsEditMode(true);
    } else {
      limsForm.resetFields();
      setIsEditMode(false);
    }
    setIsLimsModalOpen(true);
  };

  const handleLimsCancel = () => { setIsLimsModalOpen(false); limsForm.resetFields(); };

  const handleLimsSubmit = async (values) => {
    const payload = {
      lab_id: selectedLab?.id,
      lims_id: selectedLab?.lims?.id,
      lims_name: values.name,
      lims_code: values.code,
      lims_type: values.type,
      patient_registration_url: values.patient_registration_url,
      report_fetch_url: values.report_fetch_url,
      fastest_webhook_url: values.fastest_webhook_url,
      created_by: isUser.user_id,
    };
    try {
      const response = isEditMode
        ? await update_lab_lims(payload)
        : await create_lab_lims(payload);
      if (response.statusText === "OK") {
        message.success(isEditMode ? "LIMS updated!" : "LIMS added!");
        await fetchLab(setLab, baseUrl, prodTestUrl);
        limsForm.resetFields();
        setIsLimsModalOpen(false);
        setIsEditMode(false);
      } else {
        message.error(response?.data?.detail || "Failed to save LIMS.");
      }
    } catch (error) {
      message.error(error?.response?.data?.error || "An unexpected error occurred.");
    }
  };

  const showUserModal = (lab, user = null) => {
    setSelectedLabForUser(lab);
    if (user) {
      setSelectedUser(user);
      userForm.setFieldsValue({
        name: user.name,
        email: user.email,
        phone: user.phone_number || user.phone,
      });
      setIsEditMode(true);
    } else {
      setSelectedUser(null);
      userForm.resetFields();
      setIsEditMode(false);
    }
    setIsUserModalOpen(true);
  };

  const handleUserCancel = () => {
    setIsUserModalOpen(false);
    userForm.resetFields();
    setIsEditMode(false);
  };

  const handleUserSubmit = async (values) => {
    const payload = {
      lab_id: selectedLabForUser?.id,
      user_id: selectedUser?.id,
      name: values.name,
      email: values.email,
      phone: values.phone,
      created_by: isUser.user_id,
    };
    if (values.password && values.password.trim() !== "") payload.password = values.password;
    try {
      const response = isEditMode
        ? await update_lab_user(payload)
        : await create_lab_user(payload);
      if (response.statusText === "OK") {
        message.success(isEditMode ? "User updated!" : "User added!");
        await fetchLab(setLab, baseUrl, prodTestUrl);
        userForm.resetFields();
        setIsUserModalOpen(false);
        setIsEditMode(false);
      } else {
        message.error(response?.data?.detail || "Failed to save user.");
      }
    } catch (error) {
      message.error(error?.response?.data?.error || "An unexpected error occurred.");
    }
  };

  // ── camp handlers ───────────────────────────────────────────────────────────
  const handleCampStart = () => {
    setCampEditMode(false);
    setCampEditId(null);
    campAddressForm.resetFields();
    setCampSavedAddressId(null);
    setCampSavedAddressLabel("");
    setCampCoordinates([]);
    setCampEnabled(true);
    setIsCampAddressModalOpen(true);
  };

  const handleCampEdit = (record) => {
    setCampEditMode(true);
    setCampEditId(record.id);
    const existingPoints = record.coordinates_geo ?? [];
    setCampCoordinates(existingPoints);
    setCampEnabled(record.enabled ?? true);
    setIsCampMapOpen(true);
  };

  const handleCampAddressSubmit = async (values) => {
    setCampAddressLoading(true);
    try {
      const response = await create_camp_location({
        address_identifier: values.address_identifier,
        country: values.country,
        state: values.state,
        city: values.city,
        pincode: values.pincode,
        apartment_road_area: values.apartment_road_area,
        house_flat_floor_no: values.house_flat_floor_no,
        created_by: isUser.user_id,
      });
      if (response.statusText === "OK" || response.status === 201) {
        const addressId = response.data?.id;
        const label = `${values.house_flat_floor_no}, ${values.apartment_road_area}`;
        setCampSavedAddressId(addressId);
        setCampSavedAddressLabel(label);
        message.success("Address saved! Now pick coordinates on the map.");
        setIsCampAddressModalOpen(false);
        setIsCampMapOpen(true);
      } else {
        message.error(response?.data?.detail || "Failed to save address.");
      }
    } catch (err) {
      message.error(err?.response?.data?.error || "An error occurred while saving the address.");
    } finally {
      setCampAddressLoading(false);
    }
  };

  const handleCampMapSave = (drawnCoordinates) => {
    setCampCoordinates(drawnCoordinates);
    setIsCampMapOpen(false);
    handleCampCoordinatesSubmit(drawnCoordinates);
  };

  const handleCampCoordinatesSubmit = async (coords) => {
    setCampCoordLoading(true);
    try {
      let response;
      if (campEditMode && campEditId) {
        response = await update_camp_coordinates(campEditId, {
          coordinates: coords,
          enabled: campEnabled,
        });
      } else {
        if (!campSavedAddressId) {
          message.error("Address ID missing. Please restart.");
          return;
        }
        response = await create_camp_coordinates({
          address: campSavedAddressId,
          coordinates: coords,
          enabled: campEnabled,
        });
      }
      if (response.statusText === "OK" || response.status === 201) {
        message.success(
          campEditMode ? "Camp coordinates updated successfully!" : "Camp coordinates saved successfully!"
        );
        setCampCoordinates([]);
        setCampSavedAddressId(null);
        setCampSavedAddressLabel("");
        campAddressForm.resetFields();
        setCampEditMode(false);
        setCampEditId(null);
        setCampRefresh((p) => !p);
      } else {
        message.error(response?.data?.message || "Failed to save camp coordinates.");
      }
    } catch (err) {
      message.error(err?.response?.data?.error || "An error occurred while saving coordinates.");
    } finally {
      setCampCoordLoading(false);
    }
  };

  // ── shared address fields ───────────────────────────────────────────────────
  const AddressFields = () => (
    <>
      <Form.Item
        label="Address Label (e.g., home/office)"
        name="address_identifier"
        rules={[{ required: true, message: "Please enter address label" }]}
      >
        <Input placeholder="Enter address label" />
      </Form.Item>
      <Row gutter={[16, 0]}>
        <Col xs={24} sm={12}>
          <Form.Item label="Country" name="country" rules={[{ required: true, message: "Please select country" }]}>
            <Select placeholder="Select country" style={{ width: "100%" }}>
              {countries.map((c) => <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>)}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item label="State" name="state" rules={[{ required: true, message: "Please select state" }]}>
            <Select placeholder="Select state" style={{ width: "100%" }}>
              {states.map((s) => <Select.Option key={s.id} value={s.id}>{s.name}</Select.Option>)}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item label="City" name="city" rules={[{ required: true, message: "Please select city" }]}>
            <Select placeholder="Select city" style={{ width: "100%" }}>
              {cities.map((c) => <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>)}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item label="Pincode" name="pincode" rules={[{ required: true, message: "Please select pincode" }]}>
            <Select placeholder="Select pincode" style={{ width: "100%" }}>
              {pincode.map((p) => <Select.Option key={p.id} value={p.id}>{p.pin}</Select.Option>)}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item label="Area" name="apartment_road_area" rules={[{ required: true, message: "Please enter area" }]}>
            <Input placeholder="Enter area" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item label="House/Flat" name="house_flat_floor_no" rules={[{ required: true, message: "Please enter house/flat" }]}>
            <Input placeholder="Enter house/flat" />
          </Form.Item>
        </Col>
      </Row>
    </>
  );

  // ── mobile card for lab row ─────────────────────────────────────────────────
  const LabMobileCard = ({ record }) => {
    const hasCoordinates = record?.territories?.[0]?.coordinates?.length > 1;
    const location = record.location;
    const lims = record.lims;
    const labUsers = record.lab_users;

    const label = {
      fontSize: 10,
      fontWeight: 500,
      color: "var(--color-text-tertiary, #999)",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      marginBottom: 2,
    };

    return (
      <div
        style={{
          background: "var(--color-background-primary, #fff)",
          border: "0.5px solid var(--color-border-tertiary, rgba(0,0,0,0.12))",
          borderLeft: "3px solid #1677ff",
          borderRadius: 10,
          overflow: "hidden",
          marginBottom: 12,
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "12px 14px",
            background: "var(--color-background-secondary, #f9f9f7)",
            borderBottom: "0.5px solid var(--color-border-tertiary, rgba(0,0,0,0.08))",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 500, color: "var(--color-text-primary)" }}>
            {record.name}
          </div>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
        </div>

        {/* Body */}
        <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 12 }}>

          {/* Dates row */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 12px" }}>
            <div>
              <div style={label}>Onboarded</div>
              <div style={{ fontSize: 13, color: "var(--color-text-primary)" }}>
                {record.onboarded_on || "—"}
              </div>
            </div>
            <div>
              <div style={label}>Terminated</div>
              <div style={{ fontSize: 13, color: "var(--color-text-primary)" }}>
                {record.terminated_on || "—"}
              </div>
            </div>
          </div>

          {/* SPOC */}
          {record.spoc_phone_numbers?.length > 0 && (
            <div>
              <div style={label}>SPOC</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 2 }}>
                {record.spoc_phone_numbers.map((num, i) => (
                  <Tag color="blue" key={i}>{num}</Tag>
                ))}
              </div>
            </div>
          )}

          {/* Address box */}
          <div
            style={{
              background: "var(--color-background-secondary, #f5f5f3)",
              border: "0.5px solid var(--color-border-tertiary, rgba(0,0,0,0.08))",
              borderRadius: 7,
              padding: "9px 11px",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div style={label}>Address</div>
              {location && (
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0, height: "auto" }}
                  icon={<EditOutlined />}
                  onClick={() => {
                    setSelectedLab(record);
                    setIsEditAddressMode(true);
                    setSelectedAddressId(location.id);
                    addressForm.setFieldsValue({
                      address_identifier: location.address_identifier,
                      country: countries.find((c) => c.name === location.country)?.id,
                      state: states.find((s) => s.name === location.state)?.id,
                      city: cities.find((c) => c.name === location.city)?.id,
                      pincode: pincode.find((p) => p.pin === location.pincode)?.id,
                      apartment_road_area: location.apartment_road_area,
                      house_flat_floor_no: location.house_flat_floor_no,
                    });
                    setIsAddressModalOpen(true);
                  }}
                />
              )}
            </div>
            {location ? (
              <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 2 }}>
                {`${location.house_flat_floor_no}, ${location.apartment_road_area}, ${location.city}, ${location.state}, ${location.country} – ${location.pincode}`}
              </div>
            ) : (
              <Button
                type="text"
                size="small"
                style={{ padding: 0, fontSize: 12 }}
                onClick={() => {
                  setSelectedLab(record);
                  setIsEditAddressMode(false);
                  setIsAddressModalOpen(true);
                }}
              >
                Add Address <PlusCircleOutlined />
              </Button>
            )}
          </div>

          {/* LIMS box */}
          <div
            style={{
              background: "var(--color-background-secondary, #f5f5f3)",
              border: "0.5px solid var(--color-border-tertiary, rgba(0,0,0,0.08))",
              borderRadius: 7,
              padding: "9px 11px",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div style={label}>LIMS</div>
              {lims && (
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0, height: "auto" }}
                  icon={<EditOutlined />}
                  onClick={() => showLimsModal(record, lims)}
                />
              )}
            </div>
            {lims ? (
              <div style={{ marginTop: 3, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                <Tag>{lims.type === "demo" ? "Demo" : lims.type === "live" ? "Live" : lims.type}</Tag>
                <span style={{ fontSize: 13, color: "var(--color-text-primary)" }}>
                  {lims.name || lims.lims_name}
                </span>
              </div>
            ) : (
              <Button
                type="text"
                size="small"
                style={{ padding: 0, fontSize: 12 }}
                onClick={() => showLimsModal(record)}
              >
                Add LIMS <PlusCircleOutlined />
              </Button>
            )}
          </div>

          {/* Users box */}
          <div
            style={{
              background: "var(--color-background-secondary, #f5f5f3)",
              border: "0.5px solid var(--color-border-tertiary, rgba(0,0,0,0.08))",
              borderRadius: 7,
              padding: "9px 11px",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <div style={label}>Lab Users</div>
              <Button
                type="primary"
                size="small"
                onClick={(e) => { e.stopPropagation(); showUserModal(record); }}
              >
                Add User <PlusCircleOutlined />
              </Button>
            </div>
            {labUsers?.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {labUsers.map((u, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 4,
                      paddingTop: i > 0 ? 6 : 0,
                      borderTop: i > 0 ? "0.5px solid var(--color-border-tertiary)" : "none",
                    }}
                  >
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      <Tag color="green">{u.name}</Tag>
                      <Tag color="blue">{u.email}</Tag>
                      {u.phone_number && <Tag color="blue">{u.phone_number}</Tag>}
                    </div>
                    <Button
                      type="link"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={(e) => { e.stopPropagation(); showUserModal(record, u); }}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <Tag color="default">No users</Tag>
            )}
          </div>

          {/* Coordinates */}
          <div>
            <div style={label}>Coordinates</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
              {hasCoordinates ? (
                <>
                  <Button
                    size="small"
                    onClick={() => handleOpenMap(record.territories[0].coordinates)}
                  >
                    View
                  </Button>
                  <Button
                    size="small"
                    type="primary"
                    onClick={() => {
                      setSelectedLabId(record.id);
                      setSelectedCoordinates(record.territories[0].coordinates);
                      setMode("edit");
                      setIsModalOpenMap(true);
                    }}
                  >
                    Edit
                  </Button>
                </>
              ) : (
                <Button
                  size="small"
                  type="primary"
                  onClick={() => {
                    setSelectedLabId(record.id);
                    setMode("add");
                    setIsModalOpenMap(true);
                  }}
                >
                  Add Coordinates
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ── mobile card for camp row ────────────────────────────────────────────────
  const CampMobileCard = ({ record }) => {
    const addr = record.address_detail;
    const points = Array.isArray(record.coordinates_geo) ? record.coordinates_geo : [];
    const label = {
      fontSize: 10,
      fontWeight: 500,
      color: "var(--color-text-tertiary, #999)",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      marginBottom: 2,
    };

    return (
      <div
        style={{
          background: "var(--color-background-primary, #fff)",
          border: "0.5px solid var(--color-border-tertiary, rgba(0,0,0,0.12))",
          borderLeft: "3px solid #1677ff",
          borderRadius: 10,
          overflow: "hidden",
          marginBottom: 12,
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "10px 14px",
            background: "var(--color-background-secondary, #f9f9f7)",
            borderBottom: "0.5px solid var(--color-border-tertiary, rgba(0,0,0,0.08))",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)", flex: 1, minWidth: 0 }}>
            {addr
              ? `${addr.house_flat_floor_no ?? ""}, ${addr.apartment_road_area ?? ""}`
              : "Camp Location"}
          </div>
          <Switch
            checked={record.enabled}
            checkedChildren="On"
            unCheckedChildren="Off"
            size="small"
            onChange={async (val) => {
              try {
                await update_camp_coordinates(record.id, { enabled: val });
                message.success(`Camp ${val ? "enabled" : "disabled"} successfully`);
                setCampRefresh((p) => !p);
              } catch (err) {
                message.error("Failed to update status.");
              }
            }}
          />
        </div>

        {/* Body */}
        <div style={{ padding: "10px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
          {addr && (
            <div>
              <div style={label}>Full address</div>
              <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
                {`${addr.house_flat_floor_no ?? ""}, ${addr.apartment_road_area ?? ""}, ${addr.city ?? ""}, ${addr.state ?? ""}, ${addr.country ?? ""} – ${addr.pincode ?? ""}`}
              </div>
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={label}>Coordinates</div>
              {points.length > 0 ? (
                <Tag color="blue">{points.length} point{points.length !== 1 ? "s" : ""}</Tag>
              ) : (
                <Tag color="default">No coordinates</Tag>
              )}
            </div>
            <Button
              size="small"
              type="primary"
              icon={<EditOutlined />}
              onClick={() => handleCampEdit(record)}
            >
              Edit Coordinates
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // ── camp table columns ──────────────────────────────────────────────────────
  const campColumns = [
    {
      title: "Address",
      dataIndex: "address_detail",
      key: "address_detail",
      render: (addr) =>
        addr
          ? `${addr.house_flat_floor_no ?? ""}, ${addr.apartment_road_area ?? ""}, ${addr.city ?? ""}, ${addr.state ?? ""}, ${addr.country ?? ""} – ${addr.pincode ?? ""}`
          : "—",
    },
    {
      title: "Coordinates",
      dataIndex: "coordinates_geo",
      key: "coordinates_geo",
      render: (coords) => {
        const points = Array.isArray(coords) ? coords : [];
        return points.length > 0 ? (
          <Tag color="blue">{points.length} point{points.length !== 1 ? "s" : ""}</Tag>
        ) : (
          <Tag color="default">No coordinates</Tag>
        );
      },
    },
    {
      title: "Enabled",
      dataIndex: "enabled",
      key: "enabled",
      render: (enabled, record) => (
        <Switch
          checked={enabled}
          checkedChildren="Yes"
          unCheckedChildren="No"
          onChange={async (val) => {
            try {
              await update_camp_coordinates(record.id, { enabled: val });
              message.success(`Camp ${val ? "enabled" : "disabled"} successfully`);
              setCampRefresh((p) => !p);
            } catch (err) {
              message.error("Failed to update status.");
            }
          }}
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleCampEdit(record)}
        >
          Edit Coordinates
        </Button>
      ),
    },
  ];

  // ── lab table columns ───────────────────────────────────────────────────────
  const columns = [
    { title: "Lab Name", dataIndex: "name", key: "name" },
    { title: "Onboarded On", dataIndex: "onboarded_on", key: "onboarded_on" },
    {
      title: "Terminated On",
      dataIndex: "terminated_on",
      key: "terminated_on",
      render: (text) => (text ? text : "-"),
    },
    {
      title: "Single Point of Contact",
      dataIndex: "spoc_phone_numbers",
      key: "spoc_phone_numbers",
      render: (numbers) =>
        numbers?.map((number, index) => (
          <Tag color="blue" key={index}>{number}</Tag>
        )),
    },
    {
      title: "Address",
      dataIndex: "location",
      key: "location",
      render: (location, lab) =>
        location ? (
          <div>
            {`${location.house_flat_floor_no}, ${location.apartment_road_area}, ${location.city}, ${location.state}, ${location.country}, ${location.pincode}`}
            <Button
              type="link"
              onClick={() => {
                setSelectedLab(lab);
                setIsEditAddressMode(true);
                setSelectedAddressId(location.id);
                addressForm.setFieldsValue({
                  address_identifier: location.address_identifier,
                  country: countries.find((c) => c.name === location.country)?.id,
                  state: states.find((s) => s.name === location.state)?.id,
                  city: cities.find((c) => c.name === location.city)?.id,
                  pincode: pincode.find((p) => p.pin === location.pincode)?.id,
                  apartment_road_area: location.apartment_road_area,
                  house_flat_floor_no: location.house_flat_floor_no,
                });
                setIsAddressModalOpen(true);
              }}
            >
              <EditOutlined />
            </Button>
          </div>
        ) : (
          <Button
            type="text"
            onClick={() => {
              setSelectedLab(lab);
              setIsEditAddressMode(false);
              setIsAddressModalOpen(true);
            }}
          >
            Add Address <PlusCircleOutlined />
          </Button>
        ),
    },
    {
      title: "LIMS",
      dataIndex: "lims",
      key: "lims",
      render: (lims, lab) =>
        lims ? (
          <>
            <Tag>{lims.type === "demo" ? "Demo" : lims.type === "live" ? "Live" : lims.type}</Tag>{" "}
            {lims.name || lims.lims_name}{" "}
            <Button type="link" onClick={() => showLimsModal(lab, lims)}>
              <EditOutlined />
            </Button>
          </>
        ) : (
          <Button type="text" onClick={() => showLimsModal(lab)}>
            Add LIMS <PlusCircleOutlined />
          </Button>
        ),
    },
    {
      title: "View Lab User",
      dataIndex: "lab_users",
      key: "lab_users",
      render: (labUsers, lab) => (
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          {labUsers && labUsers.length > 0 ? (
            <Tooltip
              title={
                <div>
                  {labUsers.map((u, index) => (
                    <div key={index} style={{ borderBottom: "1px solid #333", padding: "4px 0" }}>
                      <Tag color="green">{u.name}</Tag>
                      <Tag color="blue">{u.email}</Tag>
                      <Tag color="blue">{u.phone_number}</Tag>
                      <Button
                        type="link"
                        onClick={(e) => { e.stopPropagation(); showUserModal(lab, u); }}
                      >
                        <EditOutlined />
                      </Button>
                    </div>
                  ))}
                </div>
              }
            >
              <Button>View ({labUsers.length})</Button>
            </Tooltip>
          ) : (
            <Tag color="default">No users</Tag>
          )}
          <Button
            type="primary"
            size="small"
            onClick={(e) => { e.stopPropagation(); showUserModal(lab); }}
          >
            Add User <PlusCircleOutlined />
          </Button>
        </div>
      ),
    },
    {
      title: "Coordinates",
      key: "coordinates",
      render: (_, record) => {
        const hasCoordinates = record?.territories?.[0]?.coordinates?.length > 1;
        return (
          <div style={{ display: "flex", gap: "8px" }}>
            {hasCoordinates ? (
              <>
                <Button onClick={() => handleOpenMap(record.territories[0].coordinates)}>
                  View Coordinates
                </Button>
                <Button
                  type="primary"
                  onClick={() => {
                    setSelectedLabId(record.id);
                    setSelectedCoordinates(record.territories[0].coordinates);
                    setMode("edit");
                    setIsModalOpenMap(true);
                  }}
                >
                  Edit Coordinates
                </Button>
              </>
            ) : (
              <Button
                type="primary"
                onClick={() => {
                  setSelectedLabId(record.id);
                  setMode("add");
                  setIsModalOpenMap(true);
                }}
              >
                Add Coordinates
              </Button>
            )}
          </div>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button type="link" onClick={() => handleEdit(record)}>
          <EditOutlined />
        </Button>
      ),
    },
  ];

  // ── render ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: isMobile ? "12px 8px" : 20 }}>
      <Tabs defaultActiveKey="labs">

        {/* ── Tab 1: Lab Partners ── */}
        <TabPane tab="Lab Partners" key="labs">
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: 16,
            }}
          >
            <Button type="primary" onClick={showModal}>
              Create Lab Partner
            </Button>
          </div>

          {/* Mobile: card list | Tablet+: scrollable table */}
          {isMobile ? (
            <div>
              {lab.length === 0 ? (
                <div style={{ textAlign: "center", color: "#aaa", padding: 32 }}>No labs found.</div>
              ) : (
                lab.map((record) => (
                  <LabMobileCard key={record.id ?? record.name} record={record} />
                ))
              )}
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <Table
                columns={columns}
                dataSource={lab}
                rowKey={(record) => record.id ?? record.name}
                size={isTablet ? "small" : "middle"}
                scroll={{ x: 1200 }}
              />
            </div>
          )}
        </TabPane>

        {/* ── Tab 2: Camp Coordinates ── */}
        <TabPane tab="Camp Coordinates" key="camp">
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: 16,
            }}
          >
            <Button type="primary" onClick={handleCampStart}>
              Add Camp Coordinates <PlusCircleOutlined />
            </Button>
          </div>

          {/* Mobile: card list | Tablet+: scrollable table */}
          {isMobile ? (
            <div>
              {campList.length === 0 ? (
                <div style={{ textAlign: "center", color: "#aaa", padding: 32 }}>No camps found.</div>
              ) : (
                campList.map((record) => (
                  <CampMobileCard key={record.id} record={record} />
                ))
              )}
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <Table
                columns={campColumns}
                dataSource={campList}
                rowKey={(record) => record.id}
                size={isTablet ? "small" : "middle"}
                scroll={{ x: 800 }}
              />
            </div>
          )}
        </TabPane>
      </Tabs>

      {/* ── Modal: Create / Edit Lab ── */}
      <Modal
        title={isEditMode ? "Edit Lab Partner" : "Create Lab Partner"}
        open={isModalOpen}
        onCancel={handleCancel}
        footer={null}
        width={isMobile ? "95vw" : 520}
        centered={isMobile}
      >
        <Form form={form} layout="vertical" onFinish={handleFormSubmit}>
          <Form.Item label="Lab Name" name="labName" rules={[{ required: true, message: "Please enter the lab name" }]}>
            <Input placeholder="Enter lab name" />
          </Form.Item>
          <Form.Item label="Onboarded On" name="onboardedOn" rules={[{ required: true, message: "Please select the onboarded date" }]}>
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="Terminated On" name="terminatedOn">
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="Single Point of Contact" name="contact" rules={[{ required: true, message: "Please enter the contact" }]}>
            <Select mode="tags" placeholder="Enter contact number" />
          </Form.Item>
          <Form.Item label="About Lab" name="aboutLab">
            <Input.TextArea rows={4} placeholder="Enter lab description" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">{isEditMode ? "Update" : "Submit"}</Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Modal: Lab Address ── */}
      <Modal
        title={`${isEditAddressMode ? "Edit" : "Add"} Address for ${selectedLab?.name}`}
        open={isAddressModalOpen}
        onCancel={handleAddressCancel}
        footer={null}
        width={isMobile ? "95vw" : 560}
        centered={isMobile}
      >
        <Form form={addressForm} layout="vertical" onFinish={handleFinishAddress}>
          <AddressFields />
          <Form.Item style={{ textAlign: "center", marginTop: 8 }}>
            <Button type="primary" style={{ marginRight: 8 }} htmlType="submit">
              {isEditAddressMode ? "Update" : "Submit"}
            </Button>
            <Button onClick={handleAddressCancel}>Cancel</Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Modal: LIMS ── */}
      <Modal
        title={`${isEditMode ? "Edit" : "Add"} LIMS for ${selectedLab?.name}`}
        open={isLimsModalOpen}
        onCancel={handleLimsCancel}
        footer={null}
        width={isMobile ? "95vw" : 520}
        centered={isMobile}
      >
        <Form layout="vertical" form={limsForm} onFinish={handleLimsSubmit}>
          <Form.Item label="Name" name="name" rules={[{ required: true, message: "Please enter the name" }]}>
            <Input placeholder="Enter name" />
          </Form.Item>
          <Form.Item label="Code" name="code" rules={[{ required: true, message: "Please enter the code" }]}>
            <Input placeholder="Enter code" />
          </Form.Item>
          <Form.Item label="Type" name="type" rules={[{ required: true, message: "Please select the type" }]}>
            <Select placeholder="Select type" options={[{ label: "Demo", value: "demo" }, { label: "Live", value: "live" }]} />
          </Form.Item>
          <Form.Item label="Patient Registration URL" name="patient_registration_url">
            <Input placeholder="Enter url" />
          </Form.Item>
          <Form.Item label="Report Fetch URL" name="report_fetch_url">
            <Input placeholder="Enter url" />
          </Form.Item>
          <Form.Item label="Fastest Webhook URL" name="fastest_webhook_url">
            <Input placeholder="Enter url" />
          </Form.Item>
          <Form.Item style={{ display: "flex", justifyContent: "flex-end", marginBottom: 0 }}>
            <Button type="primary" htmlType="submit">Submit</Button>
            <Button onClick={handleLimsCancel} style={{ marginLeft: 8 }}>Cancel</Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Modal: Lab User ── */}
      <Modal
        title={`${isEditMode ? "Edit" : "Add"} User for ${selectedLabForUser?.name}`}
        open={isUserModalOpen}
        onCancel={handleUserCancel}
        footer={null}
        width={isMobile ? "95vw" : 520}
        centered={isMobile}
      >
        <Form layout="vertical" form={userForm} onFinish={handleUserSubmit}>
          <Form.Item label="Name" name="name" rules={[{ required: true, message: "Please enter the name" }]}>
            <Input placeholder="Enter name" />
          </Form.Item>
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: "Please enter the email" },
              { type: "email", message: "Please enter a valid email" },
            ]}
          >
            <Input placeholder="Enter email" />
          </Form.Item>
          <Form.Item
            label="Phone"
            name="phone"
            rules={[
              { required: true, message: "Please enter the phone number" },
              { pattern: /^[0-9]{10}$/, message: "Enter a valid 10-digit phone number" },
            ]}
          >
            <Input placeholder="Enter phone number" />
          </Form.Item>
          {!isEditMode ? (
            <Form.Item
              label="Password"
              name="password"
              rules={[
                { required: true, message: "Please enter a password" },
                {
                  pattern: /^(?=.*[A-Z])(?=.*[!@#$%^&*])(?=.*\d).{8,}$/,
                  message: "Min 8 chars, one uppercase, one number, one special character.",
                },
              ]}
            >
              <Input.Password placeholder="Enter password" />
            </Form.Item>
          ) : (
            <>
              {!showPasswordField ? (
                <Button type="link" onClick={() => setShowPasswordField(true)} style={{ padding: 0 }}>
                  Change Password
                </Button>
              ) : (
                <Form.Item
                  label="New Password"
                  name="password"
                  rules={[{
                    pattern: /^(?=.*[A-Z])(?=.*[!@#$%^&*])(?=.*\d).{8,}$/,
                    message: "Min 8 chars, one uppercase, one number, one special character.",
                  }]}
                >
                  <Input.Password placeholder="Enter new password" />
                </Form.Item>
              )}
            </>
          )}
          <Form.Item style={{ display: "flex", justifyContent: "flex-end", marginBottom: 0 }}>
            <Button type="primary" htmlType="submit">Submit</Button>
            <Button onClick={handleUserCancel} style={{ marginLeft: 8 }}>Cancel</Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Modal: Camp Address (Step 1) ── */}
      <Modal
        title="Step 1 of 2 — Add Camp Address"
        open={isCampAddressModalOpen}
        onCancel={() => { setIsCampAddressModalOpen(false); campAddressForm.resetFields(); }}
        footer={null}
        width={isMobile ? "95vw" : 620}
        centered={isMobile}
      >
        <Form form={campAddressForm} layout="vertical" onFinish={handleCampAddressSubmit}>
          <Form.Item
            label="Address Label (e.g., camp-site / clinic)"
            name="address_identifier"
            rules={[{ required: true, message: "Please enter address label" }]}
          >
            <Input placeholder="Enter address label" />
          </Form.Item>
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item label="Country" name="country" rules={[{ required: true, message: "Please select country" }]}>
                <Select placeholder="Select country" style={{ width: "100%" }}>
                  {countries.map((c) => <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="State" name="state" rules={[{ required: true, message: "Please select state" }]}>
                <Select placeholder="Select state" style={{ width: "100%" }}>
                  {states.map((s) => <Select.Option key={s.id} value={s.id}>{s.name}</Select.Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="City" name="city" rules={[{ required: true, message: "Please select city" }]}>
                <Select placeholder="Select city" style={{ width: "100%" }}>
                  {cities.map((c) => <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Pincode" name="pincode" rules={[{ required: true, message: "Please select pincode" }]}>
                <Select placeholder="Select pincode" style={{ width: "100%" }}>
                  {pincode.map((p) => <Select.Option key={p.id} value={p.id}>{p.pin}</Select.Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Area" name="apartment_road_area" rules={[{ required: true, message: "Please enter area" }]}>
                <Input placeholder="Enter area" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="House / Flat" name="house_flat_floor_no" rules={[{ required: true, message: "Please enter house/flat" }]}>
                <Input placeholder="Enter house/flat" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Enabled">
            <Switch
              checked={campEnabled}
              onChange={(val) => setCampEnabled(val)}
              checkedChildren="Yes"
              unCheckedChildren="No"
            />
          </Form.Item>
          <Form.Item style={{ textAlign: "right", marginBottom: 0 }}>
            <Button
              style={{ marginRight: 8 }}
              onClick={() => { setIsCampAddressModalOpen(false); campAddressForm.resetFields(); }}
            >
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={campAddressLoading}>
              Save Address & Pick Coordinates →
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Maps ── */}
      <GoogleMapsProvider>
        <CampMapPicker
          isOpen={isCampMapOpen}
          onClose={() => setIsCampMapOpen(false)}
          onSave={handleCampMapSave}
          initialPoints={campEditMode ? campCoordinates : []}
        />
        <LabMapWithPolygon
          isOpen={isModalOpenMap}
          onClose={() => setIsModalOpenMap(false)}
          onSave={mode === "edit" ? handleEditCoordinates : handleSaveCoordinates}
          selectedLabId={selectedLabId}
          islat={islat}
          isLong={isLong}
          departments={departments}
          defaultDepartment={selectedDepartment}
        />
        <AllLabViewPolygonMap
          isOpen={mapOpen}
          onClose={handleCloseMap}
          labs={allLabCoordinates}
          highlightCoords={selectedCoords}
        />
      </GoogleMapsProvider>
    </div>
  );
};

export default LabPartner;